import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import {
  Button as AriaButton,
  ButtonProps as AriaButtonProps,
  Link as AriaLink,
  LinkProps as AriaLinkProps,
} from 'react-aria-components';
import { cn } from './utils';

export const agButtonVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap font-medium transition-all duration-150 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-60 disabled:cursor-not-allowed data-[disabled]:pointer-events-none data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-6 text-white shadow-xs hover:bg-brand-9 active:bg-brand-9 active:text-white-alpha-6 focus-visible:ring-ring-primary',
        secondary:
          'bg-neutral-alpha-2 text-neutral-alpha-9 shadow-xs hover:bg-neutral-alpha-4 active:bg-brand-alpha-2 active:text-neutral-alpha-8 focus-visible:ring-ring-primary',
        white:
          'bg-white text-neutral-text shadow-xs border border-neutral-5 hover:bg-neutral-2 active:bg-brand-alpha-2 active:border-neutral-5 active:text-neutral-alpha-8 focus-visible:ring-ring-primary',
        ghost:
          'bg-transparent text-neutral-text hover:bg-neutral-2 active:bg-brand-alpha-2 active:text-neutral-alpha-8 focus-visible:ring-ring-primary',
        'ghost-inverted': 'bg-transparent text-white hover:bg-white/15 focus-visible:ring-ring-primary',
        destructive:
          'bg-red-text text-white shadow-xs hover:bg-red-text/90 active:bg-red-text active:text-white-alpha-6 focus-visible:ring-ring-destructive',
        'destructive-secondary':
          'bg-red-alpha-2 text-red-text-dark shadow-xs hover:bg-red-bg active:bg-red-alpha-4 active:text-red-text-dark focus-visible:ring-ring-destructive',
        'destructive-white':
          'bg-white text-red-text border border-red-text shadow-xs hover:bg-red-bg active:bg-red-alpha-4 active:text-red-text-dark focus-visible:ring-ring-destructive',
        'destructive-ghost':
          'bg-transparent text-red-text hover:bg-red-bg active:bg-red-alpha-4 active:text-red-text-dark focus-visible:ring-ring-destructive',
        selected: 'bg-brand-1 text-brand-text border border-brand-focus focus-visible:ring-ring-primary',
      },
      size: {
        xs: 'h-[24px] px-1 text-12',
        sm: 'h-[32px] px-2.5 text-14',
        md: 'h-[40px] px-3 text-14',
        lg: 'h-[48px] px-4 text-16',
        // Icon-only sizes (square aspect ratio)
        'icon-xs': 'h-[24px] w-[24px] min-w-[24px] p-0 text-12 shrink-0',
        'icon-sm': 'h-[32px] w-[32px] min-w-[32px] p-0 text-14 shrink-0',
        'icon-md': 'h-[40px] w-[40px] min-w-[40px] p-0 text-14 shrink-0',
        'icon-lg': 'h-[48px] w-[48px] min-w-[48px] p-0 text-16 shrink-0',
      },
      shape: { rounded: 'rounded-md', square: 'rounded-none', pill: 'rounded-full', circle: 'rounded-full' },
    },
  }
);

// Button is a little bit complicated just because it can render as either a button or a link
// depending on whether an href/to prop is provided, but the styles will be the same

type ButtonVariantProps = VariantProps<typeof agButtonVariants>;

// When href is provided, it's a link
type AgButtonAsLinkProps = ButtonVariantProps & AriaLinkProps;

// When href is not provided, it's a button
type AgButtonAsButtonProps = ButtonVariantProps & AriaButtonProps;

export type AgButtonProps = AgButtonAsLinkProps | AgButtonAsButtonProps;

export const AgButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, AgButtonProps>(
  ({ className, variant = 'primary', size = 'sm', shape = 'rounded', ...props }, ref) => {
    const classes = cn(agButtonVariants({ variant, size, shape, className }));

    // If href is provided, render as AriaLink (integrates with RouterProvider for SPA navigation)
    if ('href' in props && props.href) {
      return <AriaLink className={classes} ref={ref as React.Ref<HTMLAnchorElement>} {...(props as AriaLinkProps)} />;
    }

    // Otherwise, render as AriaButton
    return (
      <AriaButton className={classes} ref={ref as React.Ref<HTMLButtonElement>} {...(props as AgButtonAsButtonProps)} />
    );
  }
);
